/**
 * Group Node Component
 *
 * Container node for grouped/compound nodes in the evidence graph.
 * Acts as a visual container for child nodes (rendered via React Flow's parentId).
 * Supports expand/collapse and resize functionality.
 */

import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, NodeResizer } from 'reactflow';
import {
  WorkspaceNodeData,
  getNodeTypeIcon,
  getNodeTypeColor,
  getNodeTypeLabel,
} from '../types';
import { formatCount } from '../../../utils/plural';

// Extended data type for group nodes
export interface GroupNodeData extends WorkspaceNodeData {
  isExpanded?: boolean;
  onToggleExpand?: (nodeId: string) => void;
  onResize?: (nodeId: string, width: number, height: number) => void;
  childCount?: number;
  initialWidth?: number;
  initialHeight?: number;
}

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 300;
const HEADER_HEIGHT = 60;

function GroupNode({ id, data, selected }: NodeProps<GroupNodeData>) {
  const {
    nodeType,
    label,
    relevanceNote,
    content,
    isExpanded: externalExpanded = true,
    onToggleExpand,
    onResize,
    childCount = 0,
    initialWidth = DEFAULT_WIDTH,
    initialHeight = DEFAULT_HEIGHT,
  } = data;

  const [isExpanded, setIsExpanded] = useState(externalExpanded);
  const nodeRef = useRef<HTMLDivElement>(null);

  const color = getNodeTypeColor(nodeType);
  const icon = getNodeTypeIcon(nodeType);
  const typeLabel = getNodeTypeLabel(nodeType);

  // Sync with external expanded state
  useEffect(() => {
    setIsExpanded(externalExpanded);
  }, [externalExpanded]);

  const handleExpandClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggleExpand?.(id);
  }, [id, isExpanded, onToggleExpand]);

  // Handle resize end
  const handleResizeEnd = useCallback(
    (_event: unknown, params: { width: number; height: number }) => {
      onResize?.(id, params.width, params.height);
    },
    [id, onResize]
  );

  // Collapsed state - show minimal header
  if (!isExpanded) {
    return (
      <div
        ref={nodeRef}
        className={`
          bg-surface rounded-lg border-2 shadow-md min-w-[220px] max-w-[300px]
          transition-all duration-200
          ${selected ? 'ring-2 ring-accent ring-offset-2' : ''}
        `}
        style={{ borderColor: color }}
      >
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-fg-tertiary border-2 border-white"
        />

        {/* Header */}
        <div
          className="px-3 py-2 rounded-t-md flex items-center gap-2"
          style={{ backgroundColor: `${color}15` }}
        >
          {/* Expand button */}
          <button
            onClick={handleExpandClick}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-surface/50 transition-colors text-fg-secondary"
            title="Expand"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Type Badge */}
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ backgroundColor: color }}
          >
            {icon}
          </div>

          <span className="text-xs font-medium uppercase tracking-wide" style={{ color }}>
            {typeLabel}
          </span>

          {childCount > 0 && (
            <span className="ml-auto text-xs text-fg-tertiary bg-surface px-1.5 py-0.5 rounded">
              {formatCount(childCount, { one: 'item', other: 'items' })}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="px-3 py-2">
          <h3 className="text-sm font-semibold text-fg mb-1 line-clamp-2">{label}</h3>
          {(content || relevanceNote) && (
            <p className="text-xs text-fg-secondary line-clamp-2">{content || relevanceNote}</p>
          )}
          <button
            onClick={handleExpandClick}
            className="mt-2 text-xs text-node-definition hover:text-node-definition font-medium flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Show {formatCount(childCount, { one: 'sub-item', other: 'sub-items' })}
          </button>
        </div>

        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 !bg-fg-tertiary border-2 border-white"
        />
      </div>
    );
  }

  // Expanded state - container for child nodes
  return (
    <div
      ref={nodeRef}
      className={`
        bg-bg-warm rounded-lg border-2 shadow-md relative
        transition-shadow duration-200
        ${selected ? 'ring-2 ring-accent ring-offset-2' : ''}
      `}
      style={{
        borderColor: color,
        minWidth: MIN_WIDTH,
        minHeight: MIN_HEIGHT,
        width: initialWidth,
        height: initialHeight,
      }}
    >
      {/* Node Resizer - only when selected */}
      <NodeResizer
        minWidth={MIN_WIDTH}
        minHeight={MIN_HEIGHT}
        isVisible={selected}
        lineClassName="border-accent-border"
        handleClassName="h-3 w-3 bg-surface border-2 border-accent-border rounded"
        onResizeEnd={handleResizeEnd}
      />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-fg-tertiary border-2 border-white z-10"
      />

      {/* Header */}
      <div
        className="px-3 py-2 rounded-t-md flex items-center gap-2 absolute top-0 left-0 right-0"
        style={{ backgroundColor: `${color}20`, height: HEADER_HEIGHT }}
      >
        {/* Collapse button */}
        <button
          onClick={handleExpandClick}
          className="w-5 h-5 flex items-center justify-center rounded hover:bg-surface/50 transition-colors text-fg-secondary"
          title="Collapse"
        >
          <svg className="w-4 h-4 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Type Badge */}
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>

        <span className="text-xs font-medium uppercase tracking-wide" style={{ color }}>
          {typeLabel}
        </span>

        {/* Label */}
        <span className="text-sm font-semibold text-fg-secondary truncate flex-1 ml-2">
          {label}
        </span>

        {childCount > 0 && (
          <span className="text-xs text-fg-tertiary bg-surface px-1.5 py-0.5 rounded">
            {formatCount(childCount, { one: 'item', other: 'items' })}
          </span>
        )}
      </div>

      {/* Content area where child nodes are rendered (via React Flow's parentId) */}
      <div
        className="absolute left-0 right-0 bottom-0"
        style={{ top: HEADER_HEIGHT }}
      >
        {/* Child nodes will be positioned here by React Flow */}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-fg-tertiary border-2 border-white z-10"
      />
    </div>
  );
}

export default memo(GroupNode);
